'use client';

import type { Dispatch, SetStateAction } from 'react';
import CompanySearchSidebar from '@/new_components/searchresults/CompanySearchSidebar';
import { SaveSearchDialog } from '@/new_components/saved-searches/SaveSearchDialog';
import type { FilterParams } from '@/lib/search-filter-params';
import type { FilterTier } from '@/lib/search-gating';
import type { useSession } from '@/hooks/use-session';
import type { NaceCode } from './searchPageTypes';
import type { SearchResultsState } from './useSearchResults';
import type { usePagingHandlers, useSavedSearchActions } from './useSearchActions';

interface SearchPageSidebarProps {
  naceCodes: NaceCode[];
  cityList: string[];
  tagList: string[];
  initialFilters: FilterParams;
  tier: FilterTier;
  session: ReturnType<typeof useSession>['data'];
  filtersOpen: boolean;
  setFiltersOpen: Dispatch<SetStateAction<boolean>>;
  results: SearchResultsState;
  paging: ReturnType<typeof usePagingHandlers>;
  saved: ReturnType<typeof useSavedSearchActions>;
  handleSubmit: (filters: FilterParams) => void;
  applyCountry: (country: string) => void;
}

/** The search page's filter sidebar and the save-search dialog it opens. */
export function SearchPageSidebar({
  naceCodes,
  cityList,
  tagList,
  initialFilters,
  tier,
  session,
  filtersOpen,
  setFiltersOpen,
  results,
  paging,
  saved,
  handleSubmit,
  applyCountry,
}: SearchPageSidebarProps) {
  const { searchResponse, currentOffset, currentSort, currentLimit } = results;
  const { handleLimitChange, handleOffsetChange } = paging;
  return (
    <>
      <CompanySearchSidebar
        naceCodes={naceCodes}
        cityList={cityList}
        tagList={tagList}
        onSubmit={handleSubmit}
        sort={currentSort}
        offset={currentOffset}
        limit={currentLimit}
        totalLimit={currentLimit}
        onTotalLimitChange={handleLimitChange}
        showFinancialYear
        total={searchResponse.total}
        onOffsetChange={handleOffsetChange}
        onCountryChange={applyCountry}
        filterParams={initialFilters}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        mobileFullscreen
        tier={tier}
        hideCompanySearch
        onSaveSearch={session ? saved.handleSaveSearch : undefined}
        savedSearches={saved.savedSearchOptions}
        onLoadSavedSearch={saved.handleLoadSavedSearch}
        isCurrentSearchSaved={saved.isCurrentSearchSaved}
      />

      {saved.pendingSave && saved.pendingSaveQuery && (
        <SaveSearchDialog
          open={saved.saveDialogOpen}
          onOpenChange={saved.setSaveDialogOpen}
          filters={saved.pendingSave}
          query={saved.pendingSaveQuery}
          sort={currentSort}
        />
      )}
    </>
  );
}
